import { useState, useEffect, useCallback } from 'react';
import { Button, Spin, notification, Input, Modal } from 'antd';
import { EditOutlined, EnvironmentOutlined, PlusOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader, useLang } from '../shared/utils';
import { useDebounce } from '../shared/hooks';
import { InfoGrid } from '../components/InfoComponents';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const EMPTY_MEMBER = {
  name_hn: '', name_eng: '', dob: '', gender: '', marital_status: '', relation: '',
  phone: '', photo: '',
};
const RELATIONS = ['पत्नी', 'पति', 'पुत्र', 'पुत्री', 'माता', 'पिता', 'भाई', 'बहन', 'दादा', 'दादी', 'नाना', 'नानी', 'अन्य'];
const GENDERS   = ['पुरुष', 'महिला', 'अन्य'];
const MARITAL   = ['विवाहित', 'अविवाहित', 'विधुर / विधवा', 'तलाकशुदा'];


// ── My Family Record Tab ─────────────────────────────────────────────────────
export function MyFamilyRecordTab() {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [editing,    setEditing]    = useState(false);
  const [newPhone,   setNewPhone]   = useState('');
  const [saving,     setSaving]     = useState(false);
  // Village search state (shown when phone not found)
  const [vilSearch,  setVilSearch]  = useState('');
  const [vilResults, setVilResults] = useState([]);
  const [vilLoading, setVilLoading] = useState(false);
  const [expanded,   setExpanded]   = useState(null); // expanded family id
  const dVilSearch = useDebounce(vilSearch, 500);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/member/my-family-record`, { headers: authHeader() });
      setData(r.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  useEffect(() => {
    if (!dVilSearch.trim()) { setVilResults([]); return; }
    setVilLoading(true);
    axios.get(`${baseUrl}/member/census-village?village=${encodeURIComponent(dVilSearch)}`, { headers: authHeader() })
      .then(r => { if (r.data.status) setVilResults(r.data.result || []); })
      .catch(() => {})
      .finally(() => setVilLoading(false));
  }, [dVilSearch]); // eslint-disable-line

  const savePhone = async () => {
    if (!/^\d{10}$/.test(newPhone)) { notification.warning({ message: '10 अंकों का नंबर दर्ज करें', placement: 'topRight' }); return; }
    setSaving(true);
    try {
      const r = await axios.patch(`${baseUrl}/member/my-family-record/phone`, { new_phone: newPhone }, { headers: authHeader() });
      if (r.data.status) { notification.success({ message: r.data.message, placement: 'topRight' }); setEditing(false); fetch(); }
      else notification.error({ message: r.data.message, placement: 'topRight' });
    } catch {} finally { setSaving(false); }
  };

  const RELATION_LABELS = { self: 'स्वयं', wife: 'पत्नी', son: 'पुत्र', daughter: 'पुत्री', father: 'पिता', mother: 'माता', brother: 'भाई', sister: 'बहन' };

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;

  if (!data?.found) return (
    <div>
      {/* Not matched notice */}
      <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#713f12', marginBottom: 4 }}>📋 आपका जनगणना रिकॉर्ड नहीं मिला</div>
        <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.6 }}>
          आपका मोबाइल नंबर 2015-17 जनगणना में नहीं मिला।<br />
          नीचे अपने गाँव का नाम खोजकर जनगणना परिवारों की बुनियादी जानकारी देख सकते हैं।
        </div>
      </div>

      {/* Village search */}
      <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <SearchOutlined style={{ color: M }} /> अपने गाँव की जनगणना खोजें
      </div>
      <Input
        prefix={<EnvironmentOutlined style={{ color: '#9ca3af' }} />}
        placeholder="गाँव का नाम लिखें जैसे: बीती, Beeti…"
        value={vilSearch}
        onChange={e => setVilSearch(e.target.value)}
        allowClear
        style={{ borderRadius: 8, marginBottom: 14 }}
      />

      {vilLoading && <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>}

      {!vilLoading && vilResults.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>{vilResults.length} परिवार मिले</div>
          {vilResults.map(f => (
            <div key={f.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 8, overflow: 'hidden' }}>
              <div
                onClick={() => setExpanded(expanded === f.id ? null : f.id)}
                style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{f.head_family || '—'}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                    {f.head_father_name ? `पिता: ${f.head_father_name}` : ''}
                    {f.village_name ? ` • ${f.village_name}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 10, background: `${M}15`, color: M, borderRadius: 20, padding: '2px 8px', fontWeight: 700 }}>
                    {f.member_count} सदस्य
                  </span>
                  <span style={{ color: '#9ca3af', fontSize: 12 }}>{expanded === f.id ? '▲' : '▼'}</span>
                </div>
              </div>

              {expanded === f.id && (
                <div style={{ padding: '0 14px 12px', borderTop: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {f.sub_caste    && <span style={{ fontSize: 10, background: '#f0fdf4', color: '#15803d', borderRadius: 20, padding: '2px 8px' }}>उपजाति: {f.sub_caste}</span>}
                    {f.kulrishi     && <span style={{ fontSize: 10, background: '#eff6ff', color: '#1d4ed8', borderRadius: 20, padding: '2px 8px' }}>कुलऋषि: {f.kulrishi}</span>}
                    {f.kildevi      && <span style={{ fontSize: 10, background: '#fdf4ff', color: '#7e22ce', borderRadius: 20, padding: '2px 8px' }}>कुलदेवी: {f.kildevi}</span>}
                    {f.tehsil_name  && <span style={{ fontSize: 10, background: '#f9fafb', color: '#374151', borderRadius: 20, padding: '2px 8px' }}>तहसील: {f.tehsil_name}</span>}
                    {f.district_name && <span style={{ fontSize: 10, background: '#f9fafb', color: '#374151', borderRadius: 20, padding: '2px 8px' }}>जिला: {f.district_name}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
                    फोन नंबर व अन्य विवरण छुपाए गए हैं। पूरी जानकारी के लिए ABRSVS कार्यालय से संपर्क करें।
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!vilLoading && vilSearch && vilResults.length === 0 && (
        <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 12 }}>
          इस नाम से कोई गाँव नहीं मिला। अलग spelling आज़माएं।
        </div>
      )}
    </div>
  );

  const { family, members } = data;

  return (
    <div>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg,${M},#b71c1c)`, borderRadius: 14, padding: '14px 16px', marginBottom: 16, color: '#fff' }}>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', marginBottom: 4, fontWeight: 600 }}>जनगणना 2015-17 · परिवार रिकॉर्ड</div>
        <div style={{ fontSize: 16, fontWeight: 900 }}>{family.head_family}</div>
        {family.head_father_name && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>पिता: {family.head_father_name}</div>}
        <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
          {family.village_name && <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '2px 9px' }}>गाँव: {family.village_name}</span>}
          {family.tehsil_name  && <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '2px 9px' }}>तहसील: {family.tehsil_name}</span>}
          {family.district_name && <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '2px 9px' }}>जिला: {family.district_name}</span>}
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
          {family.kulrishi && <span style={{ fontSize: 10, color: G, fontWeight: 700 }}>कुलऋषि: {family.kulrishi}</span>}
          {family.kildevi  && <span style={{ fontSize: 10, color: G, fontWeight: 700 }}>कुलदेवी: {family.kildevi}</span>}
          {family.sub_caste && <span style={{ fontSize: 10, color: G, fontWeight: 700 }}>उपजाति: {family.sub_caste}</span>}
        </div>
      </div>

      {/* Family members */}
      <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>
        <TeamOutlined style={{ marginRight: 6 }} />परिवार के सदस्य ({members.length})
      </div>
      {members.map((m, i) => (
        <div key={m.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '10px 14px', marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                {i + 1}. {m.member_name}
                {m.relation_type && <span style={{ marginLeft: 8, fontSize: 10, background: `${M}15`, color: M, borderRadius: 20, padding: '1px 8px', fontWeight: 700 }}>{RELATION_LABELS[m.relation_type] || m.relation_type}</span>}
              </div>
              {m.relation_name_with && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{m.relation_type} of: {m.relation_name_with}</div>}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              {m.age && <div style={{ fontSize: 11, color: '#6b7280' }}>उम्र: {m.age}</div>}
              {m.gender_type && <div style={{ fontSize: 10, color: '#9ca3af' }}>{m.gender_type}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            {m.education    && <span style={{ fontSize: 10, background: '#f0fdf4', color: '#15803d', borderRadius: 20, padding: '1px 8px' }}>{m.education}</span>}
            {m.occupation   && <span style={{ fontSize: 10, background: '#eff6ff', color: '#1d4ed8', borderRadius: 20, padding: '1px 8px' }}>{m.occupation}</span>}
            {m.married_status && <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '1px 8px' }}>{m.married_status}</span>}
          </div>
        </div>
      ))}

      {/* Update phone */}
      <div style={{ background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', marginTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>📱 अपना नंबर अपडेट करें</div>
        {editing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={newPhone} onChange={e => setNewPhone(e.target.value.replace(/\D/g,'').slice(0,10))}
              placeholder="नया 10-digit नंबर" maxLength={10}
              style={{ flex: 1, borderRadius: 8, border: '1px solid #d1d5db', padding: '6px 10px', fontSize: 13 }} />
            <Button size="small" loading={saving} onClick={savePhone} style={{ background: M, borderColor: M, color: G, borderRadius: 8, fontWeight: 700 }}>सेव</Button>
            <Button size="small" onClick={() => setEditing(false)} style={{ borderRadius: 8 }}>रद्द</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>जनगणना डेटा में नंबर गलत है? यहाँ सुधारें।</span>
            <Button size="small" onClick={() => { setEditing(true); setNewPhone(''); }} style={{ borderRadius: 8, color: M, borderColor: M }}>बदलें</Button>
          </div>
        )}
      </div>

      <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, fontSize: 11, color: '#713f12' }}>
        ⚠️ यह डेटा 2015-17 की जनगणना से है। कुछ जानकारी पुरानी हो सकती है।
      </div>
    </div>
  );
}

export function FamilyTab() {
  const [members,   setMembers]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId,    setEditId]    = useState(null);   // null = add, number = edit
  const [saving,    setSaving]    = useState(false);
  const [form,      setForm]      = useState(EMPTY_MEMBER);

  const F = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/member/family`, { headers: authHeader() });
      if (r.data.status) setMembers(r.data.result || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const openAdd  = () => { setEditId(null); setForm(EMPTY_MEMBER); setShowModal(true); };
  const openEdit = (m) => {
    setEditId(m.id);
    setForm({ m_name: m.m_name||'', m_relation_type: m.m_relation_type||'', m_gender: m.m_gender||'',
              m_dob: m.m_dob||'', m_married_status: m.m_married_status||'',
              m_education: m.m_education||'', m_occupation: m.m_occupation||'',
              m_current_address: m.m_current_address||'', m_age: m.m_age||'' });
    setShowModal(true);
  };

  const saveMember = async () => {
    if (!form.m_name.trim()) { notification.warning({ message: 'नाम आवश्यक है', placement: 'topRight' }); return; }
    setSaving(true);
    try {
      const res = editId
        ? await axios.patch(`${baseUrl}/member/family/${editId}`, form, { headers: authHeader() })
        : await axios.post(`${baseUrl}/member/family`, form, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: editId ? 'अपडेट हो गया!' : 'परिवार सदस्य जोड़ा गया!', placement: 'topRight' });
        setShowModal(false); setForm(EMPTY_MEMBER); setEditId(null); fetchMembers();
      } else notification.error({ message: res.data.message || 'Failed', placement: 'topRight' });
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Failed', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const deleteMember = async (id) => {
    try {
      await axios.delete(`${baseUrl}/member/family/${id}`, { headers: authHeader() });
      notification.success({ message: 'हटाया गया!', placement: 'topRight' });
      fetchMembers();
    } catch { notification.error({ message: 'Delete failed', placement: 'topRight' }); }
  };

  const inputStyle = { width: '100%', height: 38, borderRadius: 8, border: '1px solid #d1d5db', padding: '0 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' };
  const labelStyle = { fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4 };

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>परिवार के सदस्य ({members.length})</div>
        <Button icon={<PlusOutlined />} size="small" onClick={openAdd}
          style={{ borderRadius: 8, background: M, borderColor: M, color: G, fontWeight: 700 }}>
          सदस्य जोड़ें
        </Button>
      </div>

      {!members.length ? (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 28, textAlign: 'center' }}>
          <TeamOutlined style={{ fontSize: 36, color: '#9ca3af', marginBottom: 10 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>कोई परिवार सदस्य नहीं</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>ऊपर "सदस्य जोड़ें" से परिवार सदस्य जोड़ें।</div>
        </div>
      ) : members.map(m => (
        <div key={m.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 10, overflow: 'hidden' }}>
          <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', color: G, fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
              {(m.m_name || '?').trim()[0].toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{m.m_name || '—'}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{m.m_relation_type || ''}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => openEdit(m)} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#1d4ed8', cursor: 'pointer' }}>
                <EditOutlined /> Edit
              </button>
              <button onClick={() => deleteMember(m.id)} style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#b91c1c', cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          </div>
          <div style={{ padding: 16 }}>
            <InfoGrid fields={[
              { label: 'Gender',        value: m.m_gender },
              { label: 'Date of Birth', value: fmtDate(m.m_dob) },
              { label: 'Age',           value: m.m_age },
              { label: 'Married',       value: m.m_married_status },
              { label: 'Education',     value: m.m_education },
              { label: 'Occupation',    value: m.m_occupation },
              { label: 'Address',       value: m.m_current_address },
            ]} />
          </div>
        </div>
      ))}

      {/* ── ADD / EDIT MEMBER MODAL ── */}
      <Modal open={showModal} onCancel={() => { setShowModal(false); setForm(EMPTY_MEMBER); setEditId(null); }}
        footer={null} centered width={480}
        title={<span style={{ color: M, fontWeight: 700 }}><TeamOutlined style={{ marginRight: 8 }} />{editId ? 'सदस्य Edit करें' : 'परिवार सदस्य जोड़ें'}</span>}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: 16 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={labelStyle}>नाम *</div>
            <input value={form.m_name} onChange={F('m_name')} placeholder="पूरा नाम" style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>संबंध</div>
            <select value={form.m_relation_type} onChange={F('m_relation_type')} style={inputStyle}>
              <option value="">-- चुनें --</option>
              {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>लिंग</div>
            <select value={form.m_gender} onChange={F('m_gender')} style={inputStyle}>
              <option value="">-- चुनें --</option>
              {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>जन्म तिथि</div>
            <input type="date" value={form.m_dob} onChange={F('m_dob')} style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>वैवाहिक स्थिति</div>
            <select value={form.m_married_status} onChange={F('m_married_status')} style={inputStyle}>
              <option value="">-- चुनें --</option>
              {MARITAL.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>शिक्षा</div>
            <input value={form.m_education} onChange={F('m_education')} placeholder="जैसे: B.A., 10वीं" style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>व्यवसाय / नौकरी</div>
            <input value={form.m_occupation} onChange={F('m_occupation')} placeholder="जैसे: किसान, शिक्षक" style={inputStyle} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={labelStyle}>वर्तमान पता</div>
            <input value={form.m_current_address} onChange={F('m_current_address')} placeholder="शहर, जिला, राज्य" style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button block onClick={() => { setShowModal(false); setForm(EMPTY_MEMBER); setEditId(null); }}
            style={{ borderRadius: 8, height: 42, fontWeight: 600, color: '#6b7280', borderColor: '#d1d5db' }}>
            रद्द करें
          </Button>
          <Button block loading={saving} onClick={saveMember}
            style={{ borderRadius: 8, height: 42, fontWeight: 700, background: M, borderColor: M, color: G }}>
            {editId ? 'Save करें' : 'जोड़ें'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

